import React from 'react';
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import Translate from '../../../components/Translate';
import ChartBlock from './ChartBlock';
import ChartTooltip from './ChartTooltip';
import {formatCount, formatShare} from './format';
import './charts.css';

/*
 * A ring, its total, and a legend that carries the numbers.
 *
 * A donut rather than a full pie because the hole is where the total goes, and
 * the total is the first thing anyone wants from a chart of shares - otherwise
 * "webchat is two thirds" leaves open whether that is two thirds of nine chats
 * or of nine thousand.
 *
 * Slices at zero are dropped before drawing. recharts gives a zero slice no
 * angle but still gives it a legend row and a place in the colour order, so a
 * tariff with seven messengers and two in use produced a two-colour ring with
 * five ghosts under it.
 *
 * `data` is [{key, name, value, color}]; `name` is already translated by the
 * caller, which knows whether it is a platform (never translated) or a status
 * (always).
 */
/* recharts gives a click handler the shape's props, where the row it was drawn
   from sits on `payload` - and in some charts is also spread alongside it. The
   caller wants the row either way. */
const rowOf = (clicked) => (clicked && clicked.payload) ? clicked.payload : clicked;

const DonutChart = ({titleKey, hintKey, totalLabelKey, data = [], onSliceClick}) => {
    const slices = data.filter(item => item.value > 0);
    const total = slices.reduce((sum, item) => sum + item.value, 0);

    const legend = (
        <div className="chart-legend">
            {slices.map(item => (
                <span className="chart-legend__item" key={item.key}>
                    <span className="chart-legend__swatch" style={{backgroundColor: item.color}}/>
                    <span className="chart-legend__name">{item.name}</span>
                    <span className="chart-legend__value">
                        {formatCount(item.value)} · {formatShare(item.value, total)}
                    </span>
                </span>
            ))}
        </div>
    );

    return (
        <ChartBlock titleKey={titleKey} hintKey={hintKey} isEmpty={slices.length === 0} footer={legend}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={slices}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="58%"
                        outerRadius="82%"
                        /* a hair of space between slices, so two neighbours of
                           similar lightness still read as two */
                        paddingAngle={1.5}
                        onClick={onSliceClick ? (clicked => onSliceClick(rowOf(clicked))) : undefined}
                        cursor={onSliceClick ? 'pointer' : 'default'}
                    >
                        {slices.map(item => (
                            <Cell key={item.key} fill={item.color} stroke="none"/>
                        ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip/>}/>
                </PieChart>
            </ResponsiveContainer>

            <div className="chart-donut__centre">
                <span className="chart-donut__value">{formatCount(total)}</span>
                {totalLabelKey && (
                    <span className="chart-donut__label"><Translate text={totalLabelKey}/></span>
                )}
            </div>
        </ChartBlock>
    );
};

export default DonutChart;



// WEBPACK FOOTER //
// ./src/components-new/general/charts/DonutChart.jsx